import { useApolloClient } from '@apollo/client'
import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'
import addDays from 'date-fns/addDays'
import isBefore from 'date-fns/isBefore'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { Select } from 'components/v2/Select'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import Heading from 'components/v2/Heading'
import { CloseIcon } from '@10xLabs/react-web/components/Icons/Close'
import { InputDateTime } from 'components/InputDateTime'
import FormLayout from 'features/App/components/FormLayout'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { SEASON_TYPE } from 'features/SeasonEvents/components/SeasonType/constants'
import { seasonTypeMessages } from 'features/SeasonEvents/components/SeasonType/messages'
import { MultiServiceAutocompleteBySegments } from 'features/Services/components/MultiServiceAutocompleteBySegments'
import { IService } from 'features/Services/components/MultiServiceAutocompleteBySegments/types'
import { resolvers as loginResolvers } from 'resolvers/login'
import { getUnprocessableErrors } from 'utils/error'
import { device } from 'utils/style'
import { schema } from './schema'
import { ChangeEvent } from 'react'
import { GTSegmentSuggestion } from 'gql/types.generated'
import { MultiSegmentAutocomplete } from 'features/Segments/components/MultiSegmentAutocomplete'

const IntervalDelete = styled.div<TSFixMe>`
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translate(100%, -50%);
`

const NestedRow = styled(Row)<TSFixMe>`
  display: flex;
  flex-direction: column;

  @media ${device.laptop} {
    flex-direction: row;
  }
`

const IntervalsTitleWrapper = styled.div<TSFixMe>`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const EndsAtColumn = styled(Column)<TSFixMe>`
  align-self: flex-end;
  position: relative;
`

const StyledCloseIcon = styled(CloseIcon)<TSFixMe>`
  cursor: pointer;
`

const SEASON_TYPES_LIST = [SEASON_TYPE.L, SEASON_TYPE.H, SEASON_TYPE.S, SEASON_TYPE.E]

const messages = defineMessages({
  companyPlaceholder: {
    id: 'companies.autocomplete.placeholder',
    defaultMessage: 'Select company'
  },
  namePlaceholder: {
    id: 'dictionary.name',
    defaultMessage: 'Name'
  },
  seasonTypePlaceholder: {
    id: 'seasonEvents.form.type.placeholder',
    defaultMessage: 'Select the season'
  },
  segmentsPlaceholder: {
    id: 'seasonEvents.form.segments.placeholder',
    defaultMessage: 'Select your segments'
  },
  servicesPlaceholder: {
    id: 'seasonEvents.form.services.placeholder',
    defaultMessage: 'Select your services'
  },
  startsAtPlaceholder: {
    id: 'seasonEvents.form.startsAt.placehodlder',
    defaultMessage: 'Starts on'
  },
  endsAtPlaceholder: {
    id: 'seasonEvents.form.endsAt.placehodlder',
    defaultMessage: 'Ends on'
  }
})

const NewSeasonEventForm = (props: TSFixMe): TSFixMe => {
  const client = useApolloClient()
  client.addResolvers(loginResolvers)

  const {
    values,
    touched,
    errors,
    handleChange,
    setFieldValue,
    setValues,
    handleBlur,
    handleSubmit,
    error,
    loading,
    showCompany,
    isEdit,
    dirty
  } = props

  const intl = useIntl()

  const handleCompanyChange = (value: TSFixMe): void => {
    setValues({
      ...values,
      company: value,
      segments: []
    })
  }

  const handleTypeChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    setValues({
      ...values,
      type: event.target.value,
      services: []
    })
  }

  const addInterval = (): TSFixMe => {
    setFieldValue('activePeriods', [
      ...values.activePeriods,
      {
        startsAt: '',
        endsAt: ''
      }
    ])
  }

  const deleteInterval = (index: TSFixMe): TSFixMe => {
    setFieldValue(
      'activePeriods',
      values.activePeriods.filter((_: TSFixMe, intervalIndex: TSFixMe): TSFixMe => intervalIndex !== index)
    )
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="dictionary.event" defaultMessage="Event" />
            </Heading.H5>
          </Column>
        </Row>
        {showCompany && (
          <Row>
            <Column>
              <CompanyAutocomplete
                id="company.id"
                onChange={handleCompanyChange}
                onBlur={handleBlur}
                value={values.company.name}
                placeholder={intl.formatMessage(messages.companyPlaceholder)}
                touched={touched.company && touched.company.id}
                errors={errors.company && errors.company.id}
                label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column>
            <Input
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Select
              id="type"
              value={values.type}
              onChange={handleTypeChange}
              onBlur={handleBlur}
              touched={touched.type}
              errors={errors.type}
              label={<FormattedMessage id="seasonEvents.form.type.label" defaultMessage="Season" />}
            >
              <option value="">{intl.formatMessage(messages.seasonTypePlaceholder)}</option>
              {SEASON_TYPES_LIST.map((type): TSFixMe => {
                return (
                  <option key={type} value={type}>
                    {intl.formatMessage(seasonTypeMessages[type])}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>
        <Row>
          <Column>
            <MultiSegmentAutocomplete
              id="segments"
              values={values.segments}
              onChange={(segments: GTSegmentSuggestion[]): void => setFieldValue('segments', segments)}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.segmentsPlaceholder)}
              touched={touched.segments}
              errors={errors.segments}
              label={<FormattedMessage id="dictionary.segments" defaultMessage="Segments" />}
              companyId={values.company.id}
              skip={!values.company.id}
            />
          </Column>
        </Row>
        {values.type === SEASON_TYPE.S && (
          <Row>
            <Column>
              <MultiServiceAutocompleteBySegments
                id="services"
                values={values.services}
                onChange={(values: IService[]): void => setFieldValue('services', values)}
                onBlur={handleBlur}
                placeholder={intl.formatMessage(messages.servicesPlaceholder)}
                touched={touched.services}
                errors={errors.services}
                label={<FormattedMessage id="dictionary.services" defaultMessage="Services" />}
                segmentIds={values.segments.map(({ id }: { id: string }): string => id)}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column>
            <IntervalsTitleWrapper>
              <Heading.H5>
                <FormattedMessage id="seasonEvents.form.activePeriods.label" defaultMessage="Active Periods" />
              </Heading.H5>
              <Button kind="secondary" type="button" onClick={addInterval}>
                <FormattedMessage id="dictionary.add" defaultMessage="Add" />
              </Button>
            </IntervalsTitleWrapper>
          </Column>
        </Row>
        {values.activePeriods.map(({ startsAt, endsAt }: TSFixMe, index: TSFixMe): TSFixMe => {
          return (
            <Row key={index}>
              <Column>
                <NestedRow>
                  <Column>
                    <InputDateTime
                      id={`activePeriods[${index}].startsAt`}
                      intl={intl}
                      value={startsAt}
                      onChange={(value: TSFixMe): TSFixMe => {
                        setFieldValue(`activePeriods[${index}].startsAt`, value)

                        if (!endsAt || isBefore(endsAt, value)) {
                          setFieldValue(`activePeriods[${index}].endsAt`, endOfDay(value))
                        }
                      }}
                      disabledDays={{
                        before: new Date()
                      }}
                      touched={
                        touched.activePeriods && touched.activePeriods[index] && touched.activePeriods[index].startsAt
                      }
                      errors={
                        errors.activePeriods && errors.activePeriods[index] && errors.activePeriods[index].startsAt
                      }
                      placeholder={intl.formatMessage(messages.startsAtPlaceholder)}
                      label={
                        <FormattedMessage
                          id="seasonEvents.form.interval.label"
                          defaultMessage="Interval {count}"
                          values={{
                            count: index + 1
                          }}
                        />
                      }
                    />
                  </Column>
                  <EndsAtColumn>
                    <InputDateTime
                      id={`activePeriods[${index}].endsAt`}
                      intl={intl}
                      value={endsAt}
                      onChange={(value): TSFixMe => {
                        setFieldValue(`activePeriods[${index}].endsAt`, value)

                        // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
                        if (!startsAt || isBefore(value, startsAt)) {
                          // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
                          setFieldValue(`activePeriods[${index}].startsAt`, startOfDay(value))
                        }
                      }}
                      disabledDays={{
                        before: new Date()
                      }}
                      touched={
                        touched.activePeriods && touched.activePeriods[index] && touched.activePeriods[index].endsAt
                      }
                      errors={errors.activePeriods && errors.activePeriods[index] && errors.activePeriods[index].endsAt}
                      placeholder={intl.formatMessage(messages.endsAtPlaceholder)}
                      label=""
                    />
                    {values.activePeriods.length > 1 && (
                      <IntervalDelete>
                        <div onClick={(): TSFixMe => deleteInterval(index)}>
                          <StyledCloseIcon />
                        </div>
                      </IntervalDelete>
                    )}
                  </EndsAtColumn>
                </NestedRow>
              </Column>
            </Row>
          )
        })}
      </FormLayout.CardContent>
      <FormLayout.Split />
      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="seasonEvents.form.error"
                defaultMessage="There was an error while creating your season event"
              />
            </ErrorMessage>
          </div>
        )}
        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/season-events" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading} disabled={!dirty}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewSeasonEvent = ({
  seasonEvent = {},
  showCompany,
  onSubmit,
  error,
  loading,
  isEdit
}: TSFixMe): TSFixMe => {
  const intl = useIntl()
  const tomorrow = addDays(new Date(), 1)

  const initialSeasonEvent = {
    company: seasonEvent.company || { id: '', name: '' },
    name: seasonEvent.name || '',
    type: seasonEvent.type || '',
    activePeriods: seasonEvent.activePeriods
      ? seasonEvent.activePeriods.map(({ startsAt, endsAt }: TSFixMe): TSFixMe => {
          return {
            startsAt: new Date(startsAt),
            endsAt: new Date(endsAt)
          }
        })
      : [
          {
            startsAt: startOfDay(tomorrow),
            endsAt: endOfDay(tomorrow)
          }
        ],
    segments: seasonEvent.segments || [],
    services: seasonEvent.routeServices || []
  }

  return (
    <Formik initialValues={initialSeasonEvent} validationSchema={schema(intl, showCompany)} onSubmit={onSubmit}>
      {(props): TSFixMe => {
        return (
          <NewSeasonEventForm {...props} isEdit={isEdit} showCompany={showCompany} error={error} loading={loading} />
        )
      }}
    </Formik>
  )
}
